import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { useStore } from 'effector-react';
import { Trans, t } from '@lingui/macro';

import { CommonPageTemplate } from '@features/common';
import { Button, Progress } from '@oca/ui';
import { i18n } from '@lib/i18n';

import {
  $workforceSummary,
  $workforceFetching,
  fetchWorkforceEffect,
} from '../model/workforce';

const colors = {
  primary: '#7B44F4',
  primarySoft: '#C4B5FD',
  male: '#2563EB',
  female: '#DB2777',
  unknown: '#CBD5E1',
  track: '#F1F5F9',
  text: '#181D27',
  muted: '#64748B',
  border: '#EAECF0',
};

export function WorkforceSummaryPage() {
  const summary = useStore($workforceSummary);
  const { isLoading, isFail, error } = useStore($workforceFetching);

  React.useEffect(() => {
    fetchWorkforceEffect();
  }, []);

  const heading = i18n._(t`Saudization Index`);
  const isForbidden = isFail && error && error.status === 403;

  let content;
  if (isForbidden) {
    content = (
      <StateBox>
        <StateTitle>
          <Trans>You don&apos;t have access to the Saudization index</Trans>
        </StateTitle>
        <StateHint>
          <Trans>
            It is available to the HR team and to employees granted the
            workforce summary permission.
          </Trans>
        </StateHint>
      </StateBox>
    );
  } else if (isFail) {
    content = (
      <StateBox>
        <StateTitle>
          <Trans>The workforce summary could not be loaded</Trans>
        </StateTitle>
        <Button variant="primary" onClick={() => fetchWorkforceEffect()}>
          <Trans>Try again</Trans>
        </Button>
      </StateBox>
    );
  } else if (!summary) {
    content = isLoading ? (
      <StateBox>
        <Progress />
      </StateBox>
    ) : null;
  } else {
    content = <Summary summary={summary} />;
  }

  return (
    <CommonPageTemplate
      contentWidth={1}
      contentBackground="transparent"
      title={heading}
      heading={heading}
      actions={
        summary && !isForbidden ? (
          <HeaderActions>
            {summary.asOf && (
              <AsOf>
                <Trans>As of {dayjs(summary.asOf).format('DD MMM YYYY')}</Trans>
              </AsOf>
            )}
            <Button
              variant="secondary"
              size="small"
              disabled={isLoading}
              onClick={() => fetchWorkforceEffect()}
            >
              {isLoading ? <Trans>Refreshing…</Trans> : <Trans>Refresh</Trans>}
            </Button>
          </HeaderActions>
        ) : null
      }
    >
      {content}
    </CommonPageTemplate>
  );
}

/** Share of `value` in `total` as a whole-number percentage, for labels. */
function share(value, total) {
  return total ? Math.round(((value || 0) / total) * 100) : 0;
}

function Summary({ summary }) {
  const total = summary.totalEmployees || 0;
  // The server's own figure (2 decimals); never recomputed on the client.
  const saudization = Number(summary.saudizationPercentage) || 0;

  return (
    <React.Fragment>
      <TopGrid>
        <HeroCard>
          <Ring value={saudization} />
          <HeroBody>
            <CardLabel>
              <Trans>Saudization</Trans>
            </CardLabel>
            <HeroText>
              <Trans>
                <strong>{summary.saudiEmployees}</strong> Saudi employees out of{' '}
                <strong>{total}</strong> active employees.
              </Trans>
            </HeroText>
            <SplitList>
              <SplitItem
                color={colors.primary}
                label={<Trans>Saudi</Trans>}
                value={summary.saudiEmployees}
                total={total}
              />
              <SplitItem
                color={colors.primarySoft}
                label={<Trans>Non-Saudi</Trans>}
                value={summary.nonSaudiEmployees}
                total={total}
              />
              {summary.unknownNationalityEmployees > 0 && (
                <SplitItem
                  color={colors.unknown}
                  label={<Trans>Nationality not specified</Trans>}
                  value={summary.unknownNationalityEmployees}
                  total={total}
                />
              )}
            </SplitList>
          </HeroBody>
        </HeroCard>

        <KpiColumn>
          <KpiCard accent={colors.primary}>
            <CardLabel>
              <Trans>Total employees</Trans>
            </CardLabel>
            <KpiValue>{total}</KpiValue>
            <KpiHint>
              <Trans>Active, excluding terminated</Trans>
            </KpiHint>
          </KpiCard>
          <KpiCard accent="#F59E0B">
            <CardLabel>
              <Trans>Employees on leave</Trans>
            </CardLabel>
            <KpiValue>{summary.employeesOnLeave}</KpiValue>
            <KpiHint>
              <Trans>
                {share(summary.employeesOnLeave, total)}% of the workforce today
              </Trans>
            </KpiHint>
          </KpiCard>
        </KpiColumn>
      </TopGrid>

      <KpiRow>
        <MiniKpi
          color={colors.primary}
          label={<Trans>Saudi employees</Trans>}
          value={summary.saudiEmployees}
        />
        <MiniKpi
          color={colors.primarySoft}
          label={<Trans>Non-Saudi employees</Trans>}
          value={summary.nonSaudiEmployees}
        />
        <MiniKpi
          color={colors.male}
          label={<Trans>Male employees</Trans>}
          value={summary.maleEmployees}
        />
        <MiniKpi
          color={colors.female}
          label={<Trans>Female employees</Trans>}
          value={summary.femaleEmployees}
        />
      </KpiRow>

      <BreakdownGrid>
        <Breakdown
          title={<Trans>Gender distribution</Trans>}
          total={total}
          parts={[
            {
              key: 'male',
              label: <Trans>Male</Trans>,
              value: summary.maleEmployees,
              color: colors.male,
            },
            {
              key: 'female',
              label: <Trans>Female</Trans>,
              value: summary.femaleEmployees,
              color: colors.female,
            },
            {
              key: 'unknown',
              label: <Trans>Not specified</Trans>,
              value: summary.unknownGenderEmployees,
              color: colors.unknown,
            },
          ]}
        />
        <Breakdown
          title={<Trans>Nationality distribution</Trans>}
          total={total}
          parts={[
            {
              key: 'saudi',
              label: <Trans>Saudi</Trans>,
              value: summary.saudiEmployees,
              color: colors.primary,
            },
            {
              key: 'non-saudi',
              label: <Trans>Non-Saudi</Trans>,
              value: summary.nonSaudiEmployees,
              color: colors.primarySoft,
            },
            {
              key: 'unknown',
              label: <Trans>Not specified</Trans>,
              value: summary.unknownNationalityEmployees,
              color: colors.unknown,
            },
          ]}
        />
      </BreakdownGrid>
    </React.Fragment>
  );
}

Summary.propTypes = {
  summary: PropTypes.shape({
    asOf: PropTypes.string,
    totalEmployees: PropTypes.number,
    maleEmployees: PropTypes.number,
    femaleEmployees: PropTypes.number,
    unknownGenderEmployees: PropTypes.number,
    saudiEmployees: PropTypes.number,
    nonSaudiEmployees: PropTypes.number,
    unknownNationalityEmployees: PropTypes.number,
    employeesOnLeave: PropTypes.number,
    saudizationPercentage: PropTypes.number,
  }).isRequired,
};

const RING_SIZE = 168;
const RING_STROKE = 16;

/** Circular gauge for the Saudization percentage. */
function Ring({ value }) {
  const radius = (RING_SIZE - RING_STROKE) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.min(100, Math.max(0, value));

  return (
    <RingBox
      role="img"
      aria-label={i18n._(t`Saudization ${value} percent`)}
      style={{ width: RING_SIZE, height: RING_SIZE }}
    >
      <svg
        width={RING_SIZE}
        height={RING_SIZE}
        viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
      >
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={radius}
          fill="none"
          stroke={colors.track}
          strokeWidth={RING_STROKE}
        />
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={radius}
          fill="none"
          stroke={colors.primary}
          strokeWidth={RING_STROKE}
          strokeLinecap={clamped > 0 ? 'round' : 'butt'}
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - clamped / 100)}
          transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
        />
      </svg>
      <RingLabel>
        <RingValue>{value}%</RingValue>
        <RingCaption>
          <Trans>Saudization</Trans>
        </RingCaption>
      </RingLabel>
    </RingBox>
  );
}

Ring.propTypes = {
  value: PropTypes.number.isRequired,
};

function SplitItem({ color, label, value, total }) {
  return (
    <li>
      <Swatch style={{ backgroundColor: color }} />
      <span>{label}</span>
      <SplitValue>
        {value || 0}
        <SplitShare>{share(value, total)}%</SplitShare>
      </SplitValue>
    </li>
  );
}

SplitItem.propTypes = {
  color: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired,
  value: PropTypes.number,
  total: PropTypes.number.isRequired,
};

SplitItem.defaultProps = {
  value: 0,
};

function MiniKpi({ color, label, value }) {
  return (
    <MiniCard>
      <Swatch style={{ backgroundColor: color }} />
      <div>
        <MiniValue>{value || 0}</MiniValue>
        <MiniLabel>{label}</MiniLabel>
      </div>
    </MiniCard>
  );
}

MiniKpi.propTypes = {
  color: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired,
  value: PropTypes.number,
};

MiniKpi.defaultProps = {
  value: 0,
};

/** Counts that add up to the total, as a stacked bar with a legend. */
function Breakdown({ title, total, parts }) {
  return (
    <Card>
      <CardLabel>{title}</CardLabel>
      <Stack>
        {parts.map(part =>
          total && part.value ? (
            <span
              key={part.key}
              title={`${part.value}`}
              style={{
                width: `${(part.value / total) * 100}%`,
                backgroundColor: part.color,
              }}
            />
          ) : null,
        )}
      </Stack>
      <Legend>
        {parts.map(part => (
          <li key={part.key}>
            <Swatch style={{ backgroundColor: part.color }} />
            <span>{part.label}</span>
            <SplitValue>
              {part.value || 0}
              <SplitShare>{share(part.value, total)}%</SplitShare>
            </SplitValue>
          </li>
        ))}
      </Legend>
    </Card>
  );
}

Breakdown.propTypes = {
  title: PropTypes.node.isRequired,
  total: PropTypes.number,
  parts: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      value: PropTypes.number,
      color: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

Breakdown.defaultProps = {
  total: 0,
};

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AsOf = styled.span`
  font-size: 13px;
  color: ${colors.muted};
  white-space: nowrap;
`;

const Card = styled.section`
  padding: 20px 22px;
  background: #ffffff;
  border: 1px solid ${colors.border};
  border-radius: 14px;
  min-width: 0;
`;

const CardLabel = styled.h2`
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: ${colors.muted};
`;

const TopGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(240px, 1fr);
  grid-gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const HeroCard = styled(Card)`
  display: flex;
  align-items: center;
  gap: 32px;
  padding: 28px;
  background: linear-gradient(135deg, #ffffff 0%, #f7f3ff 100%);

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HeroBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const HeroText = styled.p`
  margin: 0 0 18px;
  font-size: 15px;
  line-height: 1.5;
  color: ${colors.text};

  strong {
    font-weight: 700;
  }
`;

const RingBox = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const RingLabel = styled.div`
  position: absolute;
  inset: 0;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const RingValue = styled.div`
  font-size: 30px;
  font-weight: 700;
  line-height: 1.1;
  color: ${colors.primary};
`;

const RingCaption = styled.div`
  margin-top: 2px;
  font-size: 12px;
  color: ${colors.muted};
`;

const SplitList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 14px;
  color: ${colors.text};

  li {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 0;
  }

  li + li {
    border-top: 1px solid ${colors.border};
  }
`;

const SplitValue = styled.span`
  margin-left: auto;
  font-weight: 600;
  white-space: nowrap;
`;

const SplitShare = styled.span`
  display: inline-block;
  min-width: 44px;
  margin-left: 10px;
  font-weight: 500;
  text-align: right;
  color: ${colors.muted};
`;

const KpiColumn = styled.div`
  display: grid;
  grid-gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  }
`;

const KpiCard = styled(Card)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  border-left: 4px solid ${({ accent }) => accent};
`;

const KpiValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  line-height: 1.15;
  color: ${colors.text};
`;

const KpiHint = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: ${colors.muted};
`;

const KpiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  grid-gap: 16px;
  margin-bottom: 16px;
`;

const MiniCard = styled(Card)`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
`;

const MiniValue = styled.div`
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
  color: ${colors.text};
`;

const MiniLabel = styled.div`
  font-size: 13px;
  color: ${colors.muted};
`;

const BreakdownGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  grid-gap: 16px;
`;

const Stack = styled.div`
  display: flex;
  height: 12px;
  margin: 12px 0 16px;
  border-radius: 6px;
  background: ${colors.track};
  overflow: hidden;

  span + span {
    border-left: 2px solid #ffffff;
  }
`;

const Legend = styled(SplitList)``;

const Swatch = styled.span`
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  border-radius: 3px;
`;

const StateBox = styled.div`
  padding: 48px 16px;
  text-align: center;
  border: 1px dashed #e2e8f0;
  border-radius: 12px;
  background: #fafbfc;
`;

const StateTitle = styled.div`
  margin-bottom: 8px;
  font-size: 15px;
  font-weight: 500;
  color: #475569;
`;

const StateHint = styled.div`
  font-size: 13px;
  color: #94a3b8;
`;
